import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Autocomplete } from '../components/Autocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ClientLink, ProjectLink } from '../components/links/EntityLinks';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Edit, Trash2, MoreVertical, Server, Loader2, AlertTriangle, Search, X, Bell, FileText, Send, MailCheck, FileSignature, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDaysUntil } from '../lib/formatUtils';
import { Pagination } from '../components/Pagination';
import { usePageSize } from '../context/PageSizeContext';
import { useDebounce } from '../lib/useDebounce';
import { useLookup } from '../lib/useLookup';
import RemindersCell from '../components/RemindersCell';
import { HostingQuoteDialog } from '../components/hosting/HostingQuoteDialog';
import { PaymentLinkDialog } from '../components/payments/PaymentLinkDialog';

const PROVIDER_FALLBACK = ['AWS', 'DigitalOcean', 'Linode', 'Hostinger', 'GoDaddy', 'Hetzner', 'Google Cloud', 'Azure', 'Other'];

const typeOptions = ['VPS', 'Shared', 'Dedicated', 'Cloud', 'Droplet', 'Managed'];
// Stable values are the backend's vocabulary — see `_add_cycle` in server.py.
// Keep monthly/quarterly/yearly first for backwards-compatibility with
// rows seeded under the old 3-option dropdown.
export const renewalCycleOptions = [
  { value: 'monthly', label: 'Monthly', months: 1, labelShort: 'Month' },
  { value: 'quarterly', label: 'Quarterly', months: 3, labelShort: 'Quarter' },
  { value: 'half_yearly', label: 'Half-Yearly', months: 6, labelShort: 'Half-Year' },
  { value: 'yearly', label: 'Yearly', months: 12, labelShort: 'Year' },
  { value: '2_years', label: '2 Years', months: 24, labelShort: '2 Years' },
  { value: '3_years', label: '3 Years', months: 36, labelShort: '3 Years' },
  { value: '4_years', label: '4 Years', months: 48, labelShort: '4 Years' },
  { value: '5_years', label: '5 Years', months: 60, labelShort: '5 Years' },
];
// AMC historically used "annually" instead of "yearly" — accept both at
// read time so legacy rows continue to render their cycle badge.
export const cycleMonths = (cycle) => {
  const norm = (cycle || 'monthly').toLowerCase();
  if (norm === 'annually' || norm === 'annual') return 12;
  const found = renewalCycleOptions.find(o => o.value === norm);
  return found ? found.months : 1;
};
export const cycleShortLabel = (cycle) => {
  const norm = (cycle || 'monthly').toLowerCase();
  if (norm === 'annually' || norm === 'annual') return 'Year';
  const found = renewalCycleOptions.find(o => o.value === norm);
  return found ? found.labelShort : 'Month';
};
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'deactivated', label: 'Deactivated' },
];
const DEFAULT_TRIGGERS = [30, 7, 1];

const emptyForm = {
  server_name: '', provider: 'AWS', type: 'VPS',
  project_id: '', project_name: '', client_id: '', client_name: '',
  ip_address: '',
  start_date: '', renewal_cycle: 'monthly', cost: '',
  disk_space: '', monthly_bandwidth: '', email_accounts: '', databases: '',
  status: 'active',
  send_reminders: false,
  reminder_emails: [],
  reminder_triggers: [...DEFAULT_TRIGGERS],
};

const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

// Compute expiry preview from start_date + cycle (client-side preview only).
// Backend re-computes authoritatively on save. Year-end-inclusive convention
// (Feb 2026): the last day INCLUSIVE of the period (start + cycle - 1 day),
// so the next renewal starts cleanly on (preview + 1 day).
const computeExpiry = (startDate, cycle) => {
  if (!startDate) return '';
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return '';
  d.setMonth(d.getMonth() + cycleMonths(cycle));
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

export const HostingPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { pageSize } = usePageSize();
  const { options: dynamicProviders } = useLookup('hosting_provider', PROVIDER_FALLBACK);
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('hosting.create');
  const canEdit = hasPermission('hosting.edit');
  const canDelete = hasPermission('hosting.delete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expiryFrom, setExpiryFrom] = useState('');
  const [expiryTo, setExpiryTo] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [emailInput, setEmailInput] = useState('');
  // Quote dialog — opens from row action menu; stays mounted so saved
  // quotes list refreshes between opens without remounting the dialog.
  const [quoteDialog, setQuoteDialog] = useState({ open: false, hosting: null });
  const [payLinkDialog, setPayLinkDialog] = useState({ open: false, hosting: null });

  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, expiryFrom, expiryTo, pageSize]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        sort_by: 'expiry_date',
        sort_order: 'asc',
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (expiryFrom) params.set('expiry_date_gte', expiryFrom);
      if (expiryTo) params.set('expiry_date_lte', `${expiryTo}T23:59:59`);
      const res = await api.get(`/hosting?${params.toString()}`);
      if (res.data.success) {
        setRows(res.data.data || []);
        setTotal(res.data.total ?? (res.data.data || []).length);
      }
    } catch {
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [page, pageSize, debouncedSearch, statusFilter, expiryFrom, expiryTo]);

  // When a project is picked, snapshot the client + seed reminder emails
  const handleProjectSelect = async (projectName) => {
    setFormData(f => ({ ...f, project_name: projectName, project_id: '' }));
    if (!projectName) return;
    try {
      const res = await api.get(`/projects?search=${encodeURIComponent(projectName)}&limit=10`);
      if (!res.data.success) return;
      const match = (res.data.data || []).find(p => (p.name || '') === projectName);
      if (!match) return;
      // Pull client emails for reminder pre-fill
      let emails = [];
      if (match.client_id) {
        try {
          const cRes = await api.get(`/clients/${match.client_id}`);
          if (cRes.data.success) {
            const c = cRes.data.data;
            if (c.email) emails.push(c.email);
            // billing_emails can be either legacy strings or the newer
            // `{email, role, label}` shape — normalise to strings so the
            // reminder chips render plain text (React error #31 otherwise).
            if (Array.isArray(c.billing_emails)) {
              c.billing_emails.forEach(entry => {
                const addr = typeof entry === 'string' ? entry : (entry && entry.email);
                if (addr && !emails.includes(addr)) emails.push(addr);
              });
            }
          }
        } catch { /* non-fatal */ }
      }
      setFormData(f => ({
        ...f,
        project_id: match.id,
        client_id: match.client_id || '',
        client_name: match.client_name || '',
        reminder_emails: f.reminder_emails && f.reminder_emails.length ? f.reminder_emails : emails,
      }));
    } catch { /* swallow */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.start_date) { toast.error('Start date is required'); return; }
    if (!formData.project_name) { toast.error('Project is required'); return; }
    try {
      const payload = {
        ...formData,
        cost: parseFloat(formData.cost) || 0,
        email_accounts: formData.email_accounts === '' ? null : parseInt(formData.email_accounts, 10),
        databases: formData.databases === '' ? null : parseInt(formData.databases, 10),
        reminder_triggers: (formData.reminder_triggers || []).map(v => parseInt(v, 10)).filter(v => !Number.isNaN(v) && v > 0).slice(0, 3),
      };
      if (isEditing && selectedItem) {
        await api.put(`/hosting/${selectedItem.id}`, payload);
        toast.success('Hosting updated');
      } else {
        await api.post('/hosting', payload);
        toast.success('Hosting added');
      }
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) { toast.error(err.response?.data?.detail || 'Operation failed'); }
  };

  const handleDelete = async (h) => {
    const confirmed = await confirm({ title: 'Remove Hosting', description: `Remove "${h.server_name}"? This cannot be undone.` });
    if (!confirmed) return;
    try { await api.delete(`/hosting/${h.id}`); toast.success('Hosting removed'); fetchData(); } catch { toast.error('Delete failed'); }
  };

  const handleRaiseProforma = (h) => {
    if (!h.project_id) {
      toast.error('No project linked — cannot raise an invoice'); return;
    }
    const description = `Hosting renewal — ${h.server_name || 'server'} for the period ${formatDate(h.start_date)} to ${formatDate(h.expiry_date)}`;
    const qs = new URLSearchParams({
      project: h.project_id,
      type: 'proforma',
      description,
      amount: String(h.cost || h.monthly_cost || 0),
    });
    navigate(`/invoices/new?${qs.toString()}`);
  };

  const handleSendReminder = async (h, mode) => {
    if (mode === 'live' && (!h.send_reminders || !(h.reminder_emails || []).length)) {
      toast.error('Renewal Reminders are off or no recipients are set on this hosting row.');
      return;
    }
    const tid = toast.loading(mode === 'test' ? 'Sending test preview…' : 'Sending reminder…');
    try {
      const body = mode === 'test' ? { mode: 'test' } : { mode: 'live' };
      const res = await api.post(`/renewals/hosting/${h.id}/send-reminder`, body);
      if (res.data.success) {
        toast.success(res.data.message, { id: tid });
      } else {
        toast.error(res.data.message || 'Send failed', { id: tid });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.detail || 'Send failed', { id: tid });
    }
  };

  const handleEdit = (h) => {
    setSelectedItem(h);
    const cycle = h.renewal_cycle || 'monthly';
    let startDate = (h.start_date || '').split('T')[0];
    if (!startDate && h.expiry_date) {
      const d = new Date(h.expiry_date);
      if (cycle === 'yearly') d.setFullYear(d.getFullYear() - 1);
      else if (cycle === 'quarterly') d.setMonth(d.getMonth() - 3);
      else d.setMonth(d.getMonth() - 1);
      startDate = d.toISOString().split('T')[0];
    }
    setFormData({
      server_name: h.server_name || '',
      provider: h.provider || 'AWS',
      type: h.type || 'VPS',
      project_id: h.project_id || '',
      project_name: h.project_name || '',
      client_id: h.client_id || '',
      client_name: h.client_name || '',
      ip_address: h.ip_address || '',
      start_date: startDate,
      renewal_cycle: cycle,
      cost: String(h.cost || h.monthly_cost || ''),
      disk_space: h.disk_space || '',
      monthly_bandwidth: h.monthly_bandwidth || '',
      email_accounts: h.email_accounts == null ? '' : String(h.email_accounts),
      databases: h.databases == null ? '' : String(h.databases),
      status: h.status || 'active',
      send_reminders: !!h.send_reminders,
      reminder_emails: Array.isArray(h.reminder_emails)
        ? h.reminder_emails.map(entry => typeof entry === 'string' ? entry : (entry && entry.email) || '').filter(Boolean)
        : [],
      reminder_triggers: Array.isArray(h.reminder_triggers) && h.reminder_triggers.length ? h.reminder_triggers : [...DEFAULT_TRIGGERS],
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const resetForm = () => { setFormData(emptyForm); setSelectedItem(null); setIsEditing(false); setEmailInput(''); };

  const addEmail = (raw) => {
    const e = (raw || '').trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error('Invalid email'); return; }
    if (formData.reminder_emails.includes(e)) return;
    setFormData(f => ({ ...f, reminder_emails: [...f.reminder_emails, e] }));
    setEmailInput('');
  };
  const removeEmail = (e) => setFormData(f => ({ ...f, reminder_emails: f.reminder_emails.filter(x => x !== e) }));
  const handleEmailKey = (e) => {
    if (['Enter', ',', ' '].includes(e.key)) { e.preventDefault(); addEmail(emailInput); }
    else if (e.key === 'Backspace' && !emailInput && formData.reminder_emails.length) {
      removeEmail(formData.reminder_emails[formData.reminder_emails.length - 1]);
    }
  };
  const updateTrigger = (idx, val) => {
    const next = [...formData.reminder_triggers];
    next[idx] = val;
    setFormData(f => ({ ...f, reminder_triggers: next }));
  };
  const addTrigger = () => {
    if (formData.reminder_triggers.length >= 3) return;
    setFormData(f => ({ ...f, reminder_triggers: [...f.reminder_triggers, 7] }));
  };
  const removeTrigger = (idx) => {
    setFormData(f => ({ ...f, reminder_triggers: f.reminder_triggers.filter((_, i) => i !== idx) }));
  };

  const renderExpiryCell = (d) => {
    if (!d) return <span className="text-muted-foreground">—</span>;
    const { label, tier } = formaDaysUntilSafe(d);
    const colorByTier = {
      expired: 'text-red-500',
      critical: 'text-red-500',
      warn: 'text-amber-500',
      ok: 'text-emerald-500',
      unknown: 'text-muted-foreground',
    };
    return (
      <div className="leading-tight">
        <div className="text-sm font-medium">{formatDate(d)}</div>
        <div className={`text-[11px] ${colorByTier[tier] || ''}`} data-testid="expiry-sublabel">{label}</div>
      </div>
    );
  };

  // Two-source preview for expiry: stored (truth, incl. renewals) +
  // computed (what backend will write on save, only shown when the
  // user touches start/cycle so it doesn't fight the stored value).
  const storedExpiry = selectedItem?.expiry_date ? selectedItem.expiry_date.slice(0, 10) : '';
  const startChanged = isEditing && selectedItem && formData.start_date !== ((selectedItem.start_date || '').slice(0, 10));
  const cycleChanged = isEditing && selectedItem && formData.renewal_cycle !== (selectedItem.renewal_cycle || 'monthly');
  const previewExpiry = computeExpiry(formData.start_date, formData.renewal_cycle);
  const showExpiryPreview = !isEditing || startChanged || cycleChanged;

  const stats = useMemo(() => {
    const totalMonthlyCost = rows.reduce((s, h) => {
      const cost = parseFloat(h.cost || h.monthly_cost) || 0;
      const months = cycleMonths(h.renewal_cycle);
      return s + cost / months;
    }, 0);
    const expiringSoon = rows.filter(h => {
      const { tier } = formaDaysUntilSafe(h.expiry_date);
      return tier === 'critical' || tier === 'warn';
    }).length;
    return { totalMonthlyCost, expiringSoon };
  }, [rows]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="hosting-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">Hosting</h1><p className="text-muted-foreground">Manage servers and hosting accounts</p></div>
        <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
          {canCreate && (<DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-hosting-btn"><Plus className="h-4 w-4 mr-2" />Add Hosting</Button></DialogTrigger>)}
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{isEditing ? 'Edit Hosting' : 'Add Hosting'}</DialogTitle><DialogDescription>Enter server/hosting details</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Project <span className="text-red-500">*</span></Label>
                <Autocomplete
                  value={formData.project_name}
                  onChange={handleProjectSelect}
                  fetchOptions={async (q) => { const res = await api.get(`/projects?search=${q}&limit=10`); return res.data.success ? res.data.data.map(p => p.name) : []; }}
                  placeholder="Search projects..."
                  data-testid="hosting-project"
                />
                {formData.client_name && (
                  <p className="text-xs text-muted-foreground">Client: <span className="text-foreground font-medium">{formData.client_name}</span></p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2"><Label>Server Name *</Label><Input value={formData.server_name} onChange={e => setFormData({ ...formData, server_name: e.target.value })} required data-testid="hosting-name" /></div>
                <div className="space-y-2"><Label>Provider</Label><Select value={formData.provider} onValueChange={v => setFormData({ ...formData, provider: v })}><SelectTrigger data-testid="hosting-provider"><SelectValue /></SelectTrigger><SelectContent>{dynamicProviders.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Type</Label><Select value={formData.type} onValueChange={v => setFormData({ ...formData, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{typeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>

                <div className="space-y-2"><Label>IP Address <span className="text-muted-foreground text-xs font-normal">(optional)</span></Label><Input value={formData.ip_address} onChange={e => setFormData({ ...formData, ip_address: e.target.value })} placeholder="0.0.0.0" data-testid="hosting-ip" /></div>
                <div className="space-y-2"><Label>Cost (INR) / {cycleShortLabel(formData.renewal_cycle)}</Label><Input type="number" value={formData.cost} onChange={e => setFormData({ ...formData, cost: e.target.value })} data-testid="hosting-cost" /></div>

                <div className="space-y-2"><Label>Start Date *</Label><Input type="date" value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} required data-testid="hosting-start-date" /></div>
                <div className="space-y-2"><Label>Renewal Cycle (Tenure)</Label><Select value={formData.renewal_cycle} onValueChange={v => setFormData({ ...formData, renewal_cycle: v })}><SelectTrigger data-testid="hosting-renewal-cycle"><SelectValue /></SelectTrigger><SelectContent>{renewalCycleOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>

                <div className="space-y-2 col-span-2"><Label>Status</Label><Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}><SelectTrigger data-testid="hosting-status"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>
              </div>

              {isEditing && storedExpiry && (
                <div className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2" data-testid="hosting-expiry-stored">
                  Expiry date: <span className="font-semibold text-foreground">{formatDate(storedExpiry)}</span>
                  <span className="ml-1 opacity-70">(from last renewal / current contract)</span>
                </div>
              )}
              {showExpiryPreview && previewExpiry && (
                <div className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2" data-testid="hosting-expiry-preview">
                  Auto-calculated expiry {isEditing ? '(after save)' : ''}: <span className="font-semibold text-foreground">{formatDate(previewExpiry)}</span>
                </div>
              )}

              <div className="pt-2 border-t border-border space-y-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Specs <span className="normal-case font-normal">(all optional)</span></p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Disk Space</Label>
                    <div className="relative">
                      <Input value={formData.disk_space} onChange={e => setFormData({ ...formData, disk_space: e.target.value })} placeholder="e.g. 20" data-testid="hosting-disk-space" className="pr-12" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground pointer-events-none">GB</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Monthly Bandwidth</Label>
                    <div className="relative">
                      <Input value={formData.monthly_bandwidth} onChange={e => setFormData({ ...formData, monthly_bandwidth: e.target.value })} placeholder="e.g. 500" data-testid="hosting-bandwidth" className="pr-12" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground pointer-events-none">GB</span>
                    </div>
                  </div>
                  <div className="space-y-2"><Label>No. of Emails</Label><Input type="number" min="0" value={formData.email_accounts} onChange={e => setFormData({ ...formData, email_accounts: e.target.value })} placeholder="e.g. 10" data-testid="hosting-email-accounts" /></div>
                  <div className="space-y-2"><Label>No. of Databases</Label><Input type="number" min="0" value={formData.databases} onChange={e => setFormData({ ...formData, databases: e.target.value })} placeholder="e.g. 5" data-testid="hosting-databases" /></div>
                </div>
              </div>

              <ReminderBlock formData={formData} setFormData={setFormData} emailInput={emailInput} setEmailInput={setEmailInput} addEmail={addEmail} removeEmail={removeEmail} handleEmailKey={handleEmailKey} updateTrigger={updateTrigger} addTrigger={addTrigger} removeTrigger={removeTrigger} />

              <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="hosting-submit-btn">{isEditing ? 'Update' : 'Add'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card><CardContent className="py-4 space-y-3">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search by server, client, IP, provider..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="hosting-search" /></div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="hosting-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Expires From</Label>
            <Input type="date" value={expiryFrom} onChange={e => setExpiryFrom(e.target.value)} className="w-[160px]" data-testid="hosting-expiry-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Expires To</Label>
            <Input type="date" value={expiryTo} onChange={e => setExpiryTo(e.target.value)} className="w-[160px]" data-testid="hosting-expiry-to" />
          </div>
          {(statusFilter !== 'all' || expiryFrom || expiryTo || search) && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); setExpiryFrom(''); setExpiryTo(''); }} data-testid="hosting-filter-reset"><X className="h-3 w-3 mr-1" />Reset</Button>
          )}
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center"><Server className="h-5 w-5 text-purple-500" /></div><div><p className="text-2xl font-bold">{total}</p><p className="text-xs text-muted-foreground">Total Servers</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Server className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold">{formatCurrency(stats.totalMonthlyCost)}</p><p className="text-xs text-muted-foreground">Monthly Equiv.</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold">{stats.expiringSoon}</p><p className="text-xs text-muted-foreground">Expiring Soon (page)</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        rows.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><Server className="h-12 w-12 mb-4 opacity-50" /><p>No hosting accounts found</p></div> :
        <Table data-testid="hosting-table"><TableHeader><TableRow><TableHead>Server</TableHead><TableHead>Provider</TableHead><TableHead>Project / Client</TableHead><TableHead>IP Address</TableHead><TableHead>Cost</TableHead><TableHead>Cycle</TableHead><TableHead>Status</TableHead><TableHead>Expires</TableHead><TableHead>Reminders</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map(h => (
          <TableRow key={h.id} data-testid={`hosting-row-${h.id}`}>
            <TableCell><div><p className="font-medium">{h.server_name}</p><p className="text-xs text-muted-foreground">{h.type}</p></div></TableCell>
            <TableCell><Badge variant="outline">{h.provider}</Badge></TableCell>
            <TableCell>
              <div className="leading-tight">
                {h.project_id ? <ProjectLink id={h.project_id} name={h.project_name} /> : <span className="text-muted-foreground text-sm">—</span>}
                {h.client_name && <div className="text-[11px] text-muted-foreground"><ClientLink id={h.client_id} name={h.client_name} /></div>}
              </div>
            </TableCell>
            <TableCell className="font-mono text-sm">{h.ip_address || '-'}</TableCell>
            <TableCell className="font-semibold">{formatCurrency(h.cost || h.monthly_cost)}</TableCell>
            <TableCell><Badge variant="outline" className="text-xs capitalize">{(h.renewal_cycle || 'monthly').replace(/_/g, ' ')}</Badge></TableCell>
            <TableCell><StatusBadge value={h.status} /></TableCell>
            <TableCell>{renderExpiryCell(h.expiry_date)}</TableCell>
            <TableCell><RemindersCell row={h} testIdPrefix="hosting-reminders" /></TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`hosting-actions-${h.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (<DropdownMenuItem onClick={() => handleEdit(h)} data-testid={`hosting-edit-${h.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                <DropdownMenuItem onClick={() => setQuoteDialog({ open: true, hosting: h })} data-testid={`hosting-quote-${h.id}`}><FileSignature className="h-4 w-4 mr-2" />Generate Quote</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPayLinkDialog({ open: true, hosting: h })} data-testid={`hosting-paylink-${h.id}`}><CreditCard className="h-4 w-4 mr-2" />Send Payment Link</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleRaiseProforma(h)} data-testid={`hosting-raise-${h.id}`} disabled={!h.project_id}><FileText className="h-4 w-4 mr-2" />Raise Proforma</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSendReminder(h, 'live')} data-testid={`hosting-send-reminder-${h.id}`} disabled={!h.send_reminders || !(h.reminder_emails || []).length}><Send className="h-4 w-4 mr-2" />Send Reminder Now</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSendReminder(h, 'test')} data-testid={`hosting-send-test-${h.id}`}><MailCheck className="h-4 w-4 mr-2" />Send Test to Me</DropdownMenuItem>
                {canDelete && (<DropdownMenuItem onClick={() => handleDelete(h)} className="text-destructive focus:text-destructive" data-testid={`hosting-delete-${h.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>}
        <div className="px-4 pb-3">
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} testIdPrefix="hosting-pagination" />
        </div>
      </CardContent></Card>

      {/* Quote builder — modal, lazily fetches saved quotes per hosting account. */}
      <HostingQuoteDialog
        open={quoteDialog.open}
        hosting={quoteDialog.hosting}
        onClose={() => setQuoteDialog({ open: false, hosting: null })}
      />

      {/* Razorpay payment link generator — clients pay online; webhook auto-extends expiry. */}
      <PaymentLinkDialog
        open={payLinkDialog.open}
        entity={payLinkDialog.hosting}
        entityType="hosting"
        onClose={() => setPayLinkDialog({ open: false, hosting: null })}
      />
    </div>
  );
};

// --- shared bits ----------------------------------------------------------
function StatusBadge({ value }) {
  const v = (value || 'active').toLowerCase();
  const cfg = {
    active: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
    expired: 'bg-red-500/10 text-red-500 border-red-500/30',
    deactivated: 'bg-muted text-muted-foreground border-border',
  };
  return <Badge variant="outline" className={`capitalize ${cfg[v] || cfg.active}`}>{v}</Badge>;
}

function ReminderBlock({ formData, setFormData, emailInput, setEmailInput, addEmail, removeEmail, handleEmailKey, updateTrigger, addTrigger, removeTrigger }) {
  return (
    <div className="pt-2 border-t border-border space-y-3">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label className="flex items-center gap-2"><Bell className="h-4 w-4 text-primary" />Renewal Reminders</Label>
          <p className="text-xs text-muted-foreground">Email the client before the renewal date. No amount, just a friendly nudge.</p>
        </div>
        <Switch checked={formData.send_reminders} onCheckedChange={(v) => setFormData(f => ({ ...f, send_reminders: v }))} data-testid="reminder-toggle" />
      </div>

      {formData.send_reminders && (
        <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3" data-testid="reminder-config">
          <div className="space-y-2">
            <Label className="text-xs">Recipient emails</Label>
            <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-border bg-background">
              {formData.reminder_emails.map(entry => {
                const e = typeof entry === 'string' ? entry : (entry && entry.email) || '';
                if (!e) return null;
                return (
                  <span key={e} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full" data-testid={`reminder-email-chip-${e}`}>
                    {e}
                    <button type="button" onClick={() => removeEmail(e)} className="hover:text-destructive" title="Remove"><X className="h-3 w-3" /></button>
                  </span>
                );
              })}
              <input
                type="email"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={handleEmailKey}
                onBlur={() => addEmail(emailInput)}
                placeholder={formData.reminder_emails.length ? 'Add another…' : 'client@example.com'}
                className="flex-1 min-w-[160px] bg-transparent outline-none text-sm"
                data-testid="reminder-email-input"
              />
            </div>
            <p className="text-[11px] text-muted-foreground">Press Enter, comma or space to add. Pre-filled from the selected project&apos;s client.</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Trigger points (days before)</Label>
              {formData.reminder_triggers.length < 3 && (
                <Button type="button" variant="ghost" size="sm" onClick={addTrigger} className="h-7 text-xs" data-testid="reminder-add-trigger"><Plus className="h-3 w-3 mr-1" />Add trigger</Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.reminder_triggers.map((t, i) => (
                <div key={i} className="flex items-center gap-1 bg-background border border-border rounded-md px-2 py-1">
                  <Input type="number" min="1" max="365" value={t} onChange={e => updateTrigger(i, e.target.value)} className="w-16 h-7 text-sm border-0 focus-visible:ring-0 p-0 text-center" data-testid={`reminder-trigger-${i}`} />
                  <span className="text-xs text-muted-foreground">d</span>
                  <button type="button" onClick={() => removeTrigger(i)} className="hover:text-destructive ml-1" title="Remove"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Maximum 3 triggers. Default: 30 / 7 / 1 days before.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Re-export so AMCPage can reuse the same reminder block + status badge
export { ReminderBlock, StatusBadge };

// safe wrapper to avoid throwing if formatDaysUntil receives undefined
function formaDaysUntilSafe(d) {
  try { return formatDaysUntil(d); } catch { return { label: '', tier: 'unknown', days: null }; }
}
